import React, { useMemo } from 'react'
import HeroImage from 'content/components/HeroImage'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidAngleLeftIcon from 'components/Luxkit/Icons/line/LineAngleLeftIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const HeroDimmedOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
  pointer-events: none;
`

const HeroContent = styled(LayoutContainer)`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BackButton = styled(TextButton)`
  position: absolute;
  top: ${rem(-16)};
  left: 0;
`

const heroImages = {
  // Celebrity Cruises
  CEL: {
    desktop: 'x23ngruxqldgrh5nlp8',
    mobile: '0b5ms5yntyp2o57o3ye',
  },
  // Cunard
  CUN: {
    desktop: 'jw2fua8cli0tglj8up2cj',
    mobile: 'z9b327t0cyykk96982oq',
  },
  // Holland America Lin
  HAL: {
    desktop: 'qo4f5iy9dckzg4l36sok',
    mobile: 'xoivyjms29pzseccql26',
  },
  // P&O Cruises Australia
  POA: {
    desktop: 'udbyke3pwqmsco7mhd5e',
    mobile: 'nz7l79ct1ddujdae0fyi',
  },
  // Princess Cruises
  PCL: {
    desktop: 'hyrer4o63tj5cqxlk4irr',
    mobile: 'axxua209r8ry158lwn3r',
  },
  // Royal Caribbean International
  RCC: {
    desktop: 'nmiblelbjmu4o2nj0m34',
    mobile: '8fciiv9efk4mvng6u7',
  },
  // Silversea Cruises
  SRX: {
    desktop: 'arwrr549lkn0rqvger5d',
    mobile: '8s7uaag5ve0llc2eum8a',
  },
  // MSC Cruises
  MSC: {
    desktop: 'dsi22rsnusucrm31vaoa',
    mobile: 'f2jv6hu40mfh9krfsczf',
  },
}

interface Props {
  cruiseLine: App.CruiseLine
}

function CruiseLineHero({ cruiseLine }: Props) {
  const heroImage = heroImages[cruiseLine.code as string] || {}
  const heroContent = useMemo(() => <>
    <HeroDimmedOverlay />

    <HeroContent>
      <BackButton
        kind="tertiary"
        href="/search/cruises"
        variant="ghost"
        startIcon={<SolidAngleLeftIcon />}
      >
        View all offers
      </BackButton>
      <Group direction="vertical" horizontalAlign="center" gap={20}>
        <Heading colour="neutral-eight" variant="heading1">
          {cruiseLine.name}
        </Heading>
      </Group>
    </HeroContent>
  </>, [cruiseLine.name])

  return <>
    <CSSBreakpoint min="tablet">
      <HeroImage
        alt={cruiseLine.name}
        fit="center"
        gravity="auto"
        id={cruiseLine.lpHeroImageId || heroImage.desktop}
        tabletAspectRatio="16:3"
      >
        {heroContent}
      </HeroImage>
    </CSSBreakpoint>

    <CSSBreakpoint max="mobile">
      <HeroImage
        alt={cruiseLine.name}
        fit="center"
        gravity="auto"
        id={cruiseLine.lpMobileHeroImageId || heroImage.mobile}
      >
        {heroContent}
      </HeroImage>
    </CSSBreakpoint>
  </>
}

export default CruiseLineHero
